import { useState, useRef, useEffect } from "react";
import { useActividadesEconomicas } from "../../../../hooks_cache/useActividadesEconomicas";
import { useMunicipios } from "../../../../hooks_cache/useMunicipio";
import { useDepartamentos } from "../../../../hooks_cache/useDepartamento";
import { useTipoDocumento } from "../../../../hooks_cache/useTipoDocumento.js";

import useNCE_JSON from "./useNCE_JSON";
import useFE_JSON from "./useFE_JSON";
import useCCFE_JSON from "./useCCFE_JSON";
import useFEX_JSON from "./useFEX_JSON";

const AMBIENTE = "00"; // 00 Prueba - 01 Produccion
const TIPO_MONEDA = "USD";

const hookMap = {
  "01": useFE_JSON,
  "03": useCCFE_JSON,
  "05": useNCE_JSON,
  11: useFEX_JSON,
};

const useDteJson = (tipoDteCodigo) => {
  const { data: actividades } = useActividadesEconomicas();
  const { data: municipios } = useMunicipios();
  const { data: departamentos } = useDepartamentos();
  const { data: tipoDocumento } = useTipoDocumento();

  const receptorPending = useRef(null);
  const emisorPending = useRef(null);

  const useHook = hookMap[tipoDteCodigo];
  if (!useHook) throw new Error(`Tipo DTE no soportado: ${tipoDteCodigo}`);

  const [factura, setFactura] = useState({
    identificacion: {
      ambiente: AMBIENTE,
      tipoDte: tipoDteCodigo,
      numeroControl: "",
      codigoGeneracion: "",
      tipoModelo: "01",
      tipoOperacion: "01",
      tipoContingencia: null,
      motivoContin: null,
      fecEmi: "",
      horEmi: "",
      tipoMoneda: TIPO_MONEDA,
    },
    documentoRelacionado: null,
    ventaTercero: null,
    emisor: null,
    receptor: null,
    cuerpoDocumento: [],
    resumen: {
      totalNoSuj: 0,
      totalExenta: 0,
      totalGravada: 0,
      subTotalVentas: 0,
      subTotal: 0,
      montoTotalOperacion: 0,
      totalLetras: "",
      condicionOperacion: 2,
    },
    extension: null,
    apendice: null,
  });

  // useEffect(() => {
  //   console.log("factura", JSON.stringify(factura, null, 2));
  // }, [factura]);

  const getActividad = (r) => actividades?.find((a) => a.id === r?.id_actividadEconomica);
  const getUbicacion = (r) => ({
    departamento: departamentos?.find((a) => a.id === r?.id_departamento)?.codigo || "",
    municipio: municipios?.find((a) => a.id === r?.id_municipio)?.codigo || "",
  });

  const { setEmisor: patchEmisor, setReceptor: patchReceptor = null, ...dteHandlers } = useHook(factura, setFactura);

  const setEmisor = (emisor) => {
    if (!municipios?.length || !departamentos?.length || !actividades?.length) {
      console.warn("Data emisor no cargada, esperando...");
      emisorPending.current = emisor;
      return;
    }

    const actividad = getActividad(emisor);
    const ubicacion = getUbicacion(emisor);
    const base = {
      nit: emisor?.nit.replace(/-/g, ""),
      nrc: emisor?.nrc.replace(/-/g, ""),
      nombre: emisor?.nombre,
      codActividad: actividad?.codigo || "",
      descActividad: actividad?.descripcion || "",
      nombreComercial: emisor?.nombreComercial || emisor?.nombre || null,
      tipoEstablecimiento: "01",
      direccion: { ...ubicacion, complemento: emisor?.direccion_completa },
      telefono: emisor?.telefono || null,
      correo: emisor?.correo,
      codEstableMH: null,
      codPuntoVentaMH: null,
      codEstable: null,
      codPuntoVenta: null,
    };

    const extendido = (typeof patchEmisor === "function" ? patchEmisor(base, emisor) : null) ?? base;

    setFactura((prev) => ({
      ...prev,
      emisor: extendido,
    }));
  };

  const setReceptor = (receptor) => {
    if (!municipios?.length || !departamentos?.length || !actividades?.length) {
      console.warn("Data receptor no cargada, esperando...");
      receptorPending.current = receptor;
      return;
    }

    const actividad = getActividad(receptor);
    const ubicacion = getUbicacion(receptor);
    const base = {
      nit: receptor.nit.replace(/-/g, ""),
      nrc: receptor.nrc.replace(/-/g, ""),
      nombre: receptor.nombre,
      codActividad: actividad?.codigo || "",
      descActividad: actividad?.descripcion || "",
      nombreComercial: receptor.nombreComercial || receptor.nombre || null,
      direccion: { ...ubicacion, complemento: receptor.direccion_completa },
      telefono: receptor.telefono || null,
      correo: receptor.correo,
    };

    const tipoDocumentoText = tipoDocumento?.find((a) => a.id === receptor.id_tipoDocumento)?.codigo || "";
    const extendido = (typeof patchReceptor === "function" ? patchReceptor(base, receptor, tipoDocumentoText) : null) ?? base;

    setFactura((prev) => ({
      ...prev,
      receptor: extendido,
    }));
  };

  const setItems = (items) => {
    setFactura((prev) => ({ ...prev, cuerpoDocumento: items }));
  };

  const setFechaHora = (fecha, hora) => {
    setFactura((prev) => ({
      ...prev,
      identificacion: {
        ...prev.identificacion,
        fecEmi: fecha,
        horEmi: hora,
      },
    }));
  };

  useEffect(() => {
    const dataCargada = municipios?.length && departamentos?.length && actividades?.length;

    if (dataCargada) {
      if (receptorPending.current) {
        console.info("Cargando receptor pendiente...");
        setReceptor(receptorPending.current);
        receptorPending.current = null;
      }

      if (emisorPending.current) {
        console.info("Cargando emisor pendiente...");
        setEmisor(emisorPending.current);
        emisorPending.current = null;
      }
    }
  }, [municipios, departamentos, actividades]);

  return {
    factura,
    setFactura,
    setEmisor,
    setReceptor,
    setItems,
    setFechaHora,
    ...dteHandlers,
  };
};

export default useDteJson;
